import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSellerStatus } from '../../hooks/useSellerStatus';
import { getStripeConnectOnboardingUrl } from '../../lib/api';
import { Button, Spinner } from '../../components/ui';

/**
 * Seller Onboarding Page
 *
 * Handles Stripe Connect Express onboarding flow:
 * 1. Shows status and CTA for users who haven't started
 * 2. Handles return from Stripe onboarding
 * 3. Redirects to /create when fully onboarded
 */
export default function SellerOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const {
    status,
    isLoading,
    canCreateRaffle,
    needsAuth,
    needsPhoneVerification,
  } = useSellerStatus();

  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  // Ref to prevent multiple redirect attempts
  const hasStartedRedirect = useRef(false);

  // Check URL params for return from Stripe
  const isSuccess = searchParams.get('success') === 'true';
  const isRefresh = searchParams.get('refresh') === 'true';

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!isLoading && needsAuth) {
      navigate('/auth/login?returnUrl=/seller/onboarding');
    }
  }, [isLoading, needsAuth, navigate]);

  // Redirect to phone verification if needed
  useEffect(() => {
    if (!isLoading && needsPhoneVerification) {
      navigate('/auth/verify-phone?returnUrl=/seller/onboarding');
    }
  }, [isLoading, needsPhoneVerification, navigate]);

  // Redirect to create page if fully onboarded
  useEffect(() => {
    if (!isLoading && canCreateRaffle) {
      navigate('/create');
    }
  }, [isLoading, canCreateRaffle, navigate]);

  // Auto-redirect pending users to Stripe to complete onboarding
  // Triggers on: refresh (link expired), or pending status without success flag
  useEffect(() => {
    if (isLoading) return;

    // Always redirect on refresh (Stripe link expired)
    if (isRefresh) {
      handleStartOnboarding();
      return;
    }

    // If pending and NOT just returned from Stripe, redirect to continue onboarding
    // (Don't auto-redirect after success return - let user see status and click continue)
    if (status === 'pending' && !isSuccess) {
      handleStartOnboarding();
    }
  }, [isRefresh, isSuccess, status, isLoading]);

  async function handleStartOnboarding() {
    // Atomic guard against multiple concurrent calls
    // Check-and-set the ref in one operation to prevent race conditions
    if (hasStartedRedirect.current) {
      return;
    }
    hasStartedRedirect.current = true;

    // Also check connecting state as a fallback (for UI consistency)
    if (connecting) {
      return;
    }

    setConnecting(true);
    setError(null);

    try {
      const result = await getStripeConnectOnboardingUrl();
      if (result.success && result.url) {
        // Redirect to Stripe - don't reset connecting state since we're leaving the page
        window.location.href = result.url;
      } else {
        setError('Failed to start onboarding. Please try again.');
        hasStartedRedirect.current = false; // Allow retry
        setConnecting(false);
      }
    } catch (err) {
      console.error('Onboarding error:', err, err?.code);
      // Show user-friendly message, but log the code for debugging
      const errorMessage = err.message || 'Something went wrong. Please try again.';
      setError(errorMessage);
      hasStartedRedirect.current = false; // Allow retry
      setConnecting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen-safe bg-midnight flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe bg-midnight">
      <div className="page-container py-12 md:py-20">
        <div className="max-w-xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 bg-primary/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <span className="text-3xl">💰</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-display text-cream mb-4">
              Set Up Payments
            </h1>
            <p className="text-cream/60">
              Connect your bank account to receive payouts when your items sell.
            </p>
          </div>

          {/* Status Card */}
          <div className="bg-midnight-light border border-midnight-lighter rounded-xl p-6 md:p-8">
            {/* Loading - connecting to Stripe */}
            {connecting && (
              <div className="text-center py-8">
                <Spinner size="lg" className="mx-auto mb-4" />
                <h2 className="text-xl font-semibold text-cream mb-2">
                  Redirecting to Stripe...
                </h2>
                <p className="text-cream/60">
                  You'll complete the setup on Stripe's secure site.
                </p>
              </div>
            )}

            {/* Success Return */}
            {!connecting && isSuccess && status === 'pending' && (
              <div className="text-center">
                <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">⏳</span>
                </div>
                <h2 className="text-xl font-semibold text-cream mb-2">
                  Almost There!
                </h2>
                <p className="text-cream/60 mb-6">
                  Your account is being verified by Stripe. This usually takes a few minutes.
                  You may need to provide additional information.
                </p>
                <Button onClick={handleStartOnboarding} loading={connecting}>
                  Continue Setup
                </Button>
              </div>
            )}

            {/* Not Started */}
            {!connecting && status === 'not_started' && !isSuccess && (
              <div className="text-center">
                <h2 className="text-xl font-semibold text-cream mb-4">
                  Connect with Stripe
                </h2>
                <p className="text-cream/60 mb-6">
                  You'll be redirected to Stripe to securely set up payments.
                  Your email and phone are already filled in.
                </p>

                <ul className="text-left space-y-3 mb-8">
                  {[
                    'Confirm your name and date of birth',
                    'Enter your address',
                    'Add your bank account (IBAN)',
                    'Verify your identity (photo of ID)',
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3 text-cream/70">
                      <svg className="w-5 h-5 text-primary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>

                <p className="text-cream/50 text-sm mb-4">
                  This is a one-time setup that takes about 3 minutes.
                </p>

                <div className="bg-midnight border border-primary/20 rounded-lg p-4 mb-6">
                  <p className="text-cream/70 text-sm">
                    <span className="text-primary">New to Stripe?</span> You'll create a free account to receive payments.
                  </p>
                  <p className="text-cream/50 text-xs mt-1">
                    Already have Stripe? You can sign in with your existing credentials.
                  </p>
                </div>

                {error && (
                  <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 mb-6">
                    <p className="text-danger text-sm">{error}</p>
                  </div>
                )}

                <Button
                  onClick={handleStartOnboarding}
                  loading={connecting}
                  size="lg"
                  className="w-full"
                >
                  {connecting ? 'Connecting...' : 'Connect with Stripe'}
                </Button>

                <p className="text-cream/40 text-xs mt-4">
                  By continuing, you agree to Stripe's{' '}
                  <a
                    href="https://stripe.com/legal/connect-account"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-cream/60"
                  >
                    Connected Account Agreement
                  </a>
                </p>
              </div>
            )}

            {/* Pending */}
            {!connecting && status === 'pending' && !isSuccess && !isRefresh && (
              <div className="text-center">
                <div className="w-12 h-12 bg-warning/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">⏳</span>
                </div>
                <h2 className="text-xl font-semibold text-cream mb-2">
                  Setup Incomplete
                </h2>
                <p className="text-cream/60 mb-6">
                  You started setting up payments but didn't finish.
                  Continue where you left off to start selling.
                </p>

                {error && (
                  <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 mb-6">
                    <p className="text-danger text-sm">{error}</p>
                  </div>
                )}

                <Button onClick={handleStartOnboarding} loading={connecting}>
                  Continue Setup
                </Button>
              </div>
            )}

            {/* Restricted */}
            {!connecting && status === 'restricted' && (
              <div className="text-center">
                <div className="w-12 h-12 bg-danger/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl">⚠️</span>
                </div>
                <h2 className="text-xl font-semibold text-cream mb-2">
                  Action Required
                </h2>
                <p className="text-cream/60 mb-6">
                  Stripe needs additional information to verify your account.
                  Please complete the verification to continue.
                </p>

                {error && (
                  <div className="bg-danger/10 border border-danger/20 rounded-lg p-4 mb-6">
                    <p className="text-danger text-sm">{error}</p>
                  </div>
                )}

                <Button onClick={handleStartOnboarding} loading={connecting}>
                  Complete Verification
                </Button>
              </div>
            )}
          </div>

          {/* Benefits */}
          <div className="mt-10 grid grid-cols-3 gap-4 text-center">
            {[
              { icon: '🔒', label: 'Secure' },
              { icon: '⚡', label: 'Fast Payouts' },
              { icon: '🌍', label: 'Irish Banks' },
            ].map(({ icon, label }) => (
              <div key={label} className="text-cream/50">
                <span className="text-2xl block mb-1">{icon}</span>
                <span className="text-sm">{label}</span>
              </div>
            ))}
          </div>

          {/* Back Link */}
          <div className="text-center mt-8">
            <Link to="/" className="text-cream/50 text-sm hover:text-cream transition-colors">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
